import { isEmpty, isNull } from 'lodash';

import { SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import { fullName } from '@lib/utils';
import { IMoment, moment } from '@lib/moment';

import { DRIVER_SUGGESTED_BY_MAPPING, IServerSuggestedDriver, ISuggestedDriver } from '@services/trips-list';
import { DriverLabelColor, DriverStatus, IDriver, IDriverTagData, IDriverTagLabelAndTooltipData, IMapDriverTagDataInput } from './driver-tag.interface';

import { DriverIdOriginTags } from '@lib/data/driver-identification';
import { AI_SUGGESTION_PARAMS } from '@lib/data/driving-period-view-model';

export class DriverTag {
  private driverTagData: IDriverTagData;
  private driverIdOriginEnabled: boolean;
  private isRestricted: boolean;
  private restrictedDriverLabel: string;
  private driverStatus: DriverStatus = DriverStatus.UNIDENTIFIED;

  private labelText: string;
  private tooltipText: string;
  private labelColor: DriverLabelColor;
  private imageUrl: string;
  private imageTimestamp: IMoment;

  constructor(
    driverTagData: IDriverTagData,
    driverIdOriginEnabled: boolean = false,
    isRestricted: boolean = false,
    restrictedDriverLabel: string,
    private sanitizer: DomSanitizer,
  ) {
    this.driverTagData = driverTagData;
    this.driverIdOriginEnabled = driverIdOriginEnabled;
    this.isRestricted = isRestricted;
    this.restrictedDriverLabel = restrictedDriverLabel;

    this.initialize();
  }

  public getLabelAndTooltipData(): IDriverTagLabelAndTooltipData {
    return {
      labelText: this.labelText,
      tooltipText: this.tooltipText,
      labelColor: this.labelColor,
      driverStatus: this.driverStatus,
      imageUrl: this.imageUrl,
      imageTimestamp: this.imageTimestamp,
    };
  }

  public getNoPermissionsLabelAndTooltipData(): IDriverTagLabelAndTooltipData {
    const noPermissionsTooltip = $localize`You don\'t have the permission to view this driver.`;

    return {
      labelText: this.labelText,
      tooltipText: noPermissionsTooltip,
      labelColor: DriverLabelColor.INACTIVE,
      driverStatus: this.driverStatus,
    };
  }

  private initialize(): void {
    this.determineDriverStatus();
    this.setLabelAndTooltip();
    this.setImageUrlAndTimestamp();
  }

  private determineDriverStatus(): void {
    const { driver, suggestedDriver } = this.driverTagData;

    const driverExists = !!(driver && driver.id);
    const suggestedDriverExists = !isEmpty(suggestedDriver) && !isNull(suggestedDriver);
    const isSuggestedDriverFacialProfile = suggestedDriverExists && !suggestedDriver.id && !!suggestedDriver.profileId;

    if (driverExists) {
      if (suggestedDriverExists && driver.id === suggestedDriver.id) {
        this.driverStatus = DriverStatus.AI_ASSIGNED;
      } else {
        this.driverStatus = DriverStatus.ASSIGNED;
      }
    } else if (isSuggestedDriverFacialProfile) {
      this.driverStatus = DriverStatus.NON_COMPANY_OR_UNFAMILIAR;
    } else if (suggestedDriverExists && suggestedDriver.id) {
      this.driverStatus = DriverStatus.AI_IDENTIFIED;
    } else {
      this.driverStatus = DriverStatus.UNIDENTIFIED;
    }
  }

  private setLabelAndTooltip(): void {
    const { driver, suggestedDriver } = this.driverTagData;

    if (this.isRestricted) {
      this.labelText = driver?.name || this.restrictedDriverLabel;
      this.tooltipText = $localize`You don't have the permission to view this driver.`;
      this.labelColor = DriverLabelColor.INACTIVE;
      return;
    }

    switch (this.driverStatus) {
    case DriverStatus.AI_ASSIGNED:
      this.labelText = driver.name;
      this.labelColor = DriverLabelColor.ACCENT;
      if (this.isDriverIdentifiedOrAssignedViaSmartTripMatch()) {
        this.tooltipText = this.getSmartTripMatchTooltipText();
      }
      else {
        this.tooltipText = $localize`Driver assigned via Face Match.`;
      }
      break;

    case DriverStatus.AI_IDENTIFIED:
      this.labelText = suggestedDriver.name;
      this.labelColor = DriverLabelColor.CAUTION;
      if (this.isDriverIdentifiedOrAssignedViaSmartTripMatch()) {
        this.tooltipText = this.getSmartTripMatchTooltipText();
      } else {
        this.tooltipText = $localize`Driver identified via Face Match.`;
      }
      break;

    case DriverStatus.NON_COMPANY_OR_UNFAMILIAR:
      this.labelText = $localize`Facial profile detected`;
      this.tooltipText = $localize`Driver identified via Face Match.`;
      this.labelColor = DriverLabelColor.CAUTION;
      break;

    case DriverStatus.UNIDENTIFIED:
      this.labelText = $localize`Unidentified driver`;
      this.tooltipText = $localize`No driver has been identified.`;
      this.labelColor = DriverLabelColor.CAUTION;
      break;

    case DriverStatus.ASSIGNED:
      this.setAssignedDriverLabelAndTooltip();
      break;

    default:
      this.labelText = $localize`Unidentified driver`;
      this.labelColor = DriverLabelColor.CAUTION;
      break;
    }
  }

  private setAssignedDriverLabelAndTooltip(): void {
    const { driver, driverIdOriginTag, dispatcherName, vehicleNumber } = this.driverTagData;

    this.labelText = driver.name;
    this.labelColor = DriverLabelColor.ACCENT;

    if (this.driverIdOriginEnabled && driverIdOriginTag) {
      switch (driverIdOriginTag) {
      case DriverIdOriginTags.faceMatch:
        this.tooltipText = $localize`Driver assigned via Face Match.`;
        break;

      case DriverIdOriginTags.rfid:
        this.tooltipText = $localize`Driver assigned via Driver ID Reader.`;
        break;

      case DriverIdOriginTags.driverClaimed:
        this.tooltipText = $localize`Driver claimed this unidentified trip from the Driver App.`;
        break;

      case DriverIdOriginTags.driverConnected:
        this.tooltipText = $localize`Driver connected via Driver App or permanently assigned to ${vehicleNumber || 'vehicle'}.`;
        break;

      case DriverIdOriginTags.manuallyAssigned:
        this.tooltipText = $localize`Driver assigned by ${dispatcherName || 'fleet user'}.`;
        break;

      default:
        this.tooltipText = '';
      }
    } else {
      this.tooltipText = '';
    }
  }

  private setImageUrlAndTimestamp(): void {
    if (this.isRestricted) return;

    const suggestedDriver = this.driverTagData?.suggestedDriver;

    this.imageUrl = suggestedDriver?.boundedImageUrl || '';
    this.imageTimestamp = suggestedDriver?.imageCapturedAt
      ? moment(this.sanitizer.sanitize(SecurityContext.HTML, suggestedDriver.imageCapturedAt))
      : null;
  }

  private isDriverIdentifiedOrAssignedViaSmartTripMatch(): boolean {
    const { suggestedDriver } = this.driverTagData;
    return suggestedDriver?.suggestedBy === DRIVER_SUGGESTED_BY_MAPPING.smart_trip_matching;
  }
  
  private getSmartTripMatchTooltipText(): string {
    const assignmentStatus = this.driverStatus === DriverStatus.AI_ASSIGNED ? $localize`assigned` : $localize`identified`;
    const baseText = $localize`Driver ${assignmentStatus} via AI Driver App data`;

    const aiSuggestionParams = this.driverTagData.suggestedDriver?.aiSuggestionParams;
    if (!aiSuggestionParams?.length) return `${baseText}.`;

    return baseText + $localize` based on ` + aiSuggestionParams.join(', ') + '.';
  }
}

export const mapDriverTagData = ({
  serverDriver,
  serverSuggestedDriver,
  dispatcherName,
  vehicleNumber,
  driverIdOriginTag,
}: IMapDriverTagDataInput): IDriverTagData => {
  const serverDriverExists = !!(serverDriver && serverDriver.id);
  const driver: IDriver | null = serverDriverExists
    ? {
      id: serverDriver.id,
      name: fullName(serverDriver),
      groupIds: serverDriver.group_ids,
      driverCompanyId: serverDriver.driver_company_id,
    } : null;

  const serverSuggestedDriverExists = !isEmpty(serverSuggestedDriver) && !isNull(serverSuggestedDriver);
  const isValidSuggestedDriver =
    serverSuggestedDriverExists && (serverSuggestedDriver.id || serverSuggestedDriver.driver_facial_profile_id);

  const suggestedDriver: ISuggestedDriver | null = isValidSuggestedDriver
    ? {
        id: serverSuggestedDriver.id,
        profileId: serverSuggestedDriver.driver_facial_profile_id,
        boundedImageUrl: serverSuggestedDriver.bounded_image_url,
        imageCapturedAt: moment(serverSuggestedDriver.image_datetime),
        name: fullName(serverSuggestedDriver),
        driverCompanyId: serverSuggestedDriver.driver_company_id ?? '',
        suggestedBy: DRIVER_SUGGESTED_BY_MAPPING[serverSuggestedDriver.suggested_by],
        dutyStatus: serverSuggestedDriver.duty_status,
        eldMode: serverSuggestedDriver.eld_mode,
        aiSuggestionParams: Object.keys(AI_SUGGESTION_PARAMS)
          .filter((key: string): boolean => serverSuggestedDriver[key])
          .map((key: string): string => AI_SUGGESTION_PARAMS[key]),
      }
    : null;

  return {
    driver,
    suggestedDriver,
    dispatcherName,
    vehicleNumber,
    driverIdOriginTag,
  };
};